<template>
  <v-dialog
    v-model="isOpen"
    width="700"
  >
    <v-card>
      <v-card-title class="subtitle-1 justify-end pb-0">
        <v-icon @click="close">mdi-window-close</v-icon>
      </v-card-title>
      <v-card-title class="subtitle-1 text-uppercase justify-center pt-0 pb-0">Retargeting Request</v-card-title>
      <v-card-title class="pt-0 mb-5">
        <v-row class="justify-center">
          <v-progress-linear
            v-show="buttonClicked"
            indeterminate
          ></v-progress-linear>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row class="justify-center">
          <v-col md="9">
            <v-text-field
              name="requestName"
              label="Name"
              id="RetargetingRequestName"
              v-model="requestName"
              :rules="retargetingRequestNameRules"
              ref="requestName"
              :readonly="buttonClicked"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="justify-center">
          <v-col
            cols="6"
            md="3"
          >
            <v-text-field
              name="placementId"
              label="FW Placement ID"
              id="FWPlacementId"
              v-model="FWPlacementID"
              :rules="placementIdRules"
              ref="placementId"
              :readonly="buttonClicked"
            ></v-text-field>
          </v-col>
          <v-col
            cols="6"
            md="6"
            class="d-flex align-center"
          >
            <v-menu
              ref="startDateMenu"
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                :max="endDate"
                no-title
                @input="handleStartDateChange"
              ></v-date-picker>
            </v-menu>
            <span class="separator">-</span>
            <v-menu
              ref="endDateMenu"
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                :min="startDate"
                no-title
                @input="handleEndDateChange"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-center pb-8">
        <action-button
          light
          :disabled="!isFormValid || buttonClicked"
          @click.native="retargetingRequest"
          label="Submit"
          color="primary"
          id="requestVideoAmpAudienceBtn"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import XandrAPI from '@/api/XandrAPI'
  import ActionButton from '@/components/common/actionButton'
  import Toast from '@/components/common/toasts'
  import DateFromTo from '@/components/common/dateRange/DateFromTo'

  export default {
    components: {
      ActionButton,
      DateFromTo,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isOpen: this.value,
        FWPlacementID: null,
        requestName: null,
        buttonClicked: false,
        startDate: null,
        endDate: null,
        startDateMenu: false,
        endDateMenu: false,
        valid: true,
        retargetingRequestNameRules: [
          v => !!v || 'Name is required',
          v => /^[A-Za-z0-9_]+$/.test(v) || 'Allowed characters are A-Z, a-z, 0-9, _',
          v => (v && v.length >= 6) || 'Name must be at least 6 characters',
        ],
        placementIdRules: [v => !!v || 'Placement ID is required', v => /^\d+$/.test(v) || 'Only numbers are allowed'],
      }
    },
    computed: {
      isFormValid() {
        return (
          this.retargetingRequestNameRules.every(rule => rule(this.requestName) === true) &&
          this.placementIdRules.every(rule => rule(this.FWPlacementID) === true)
        )
      },
    },
    methods: {
      handleStartDateChange(value) {
        this.startDate = value // update start date
        this.startDateMenu = false // close start date picker
        if (this.endDate < value) {
          this.endDate = null
        }
      },
      handleEndDateChange(value) {
        this.endDate = value // update end date
        this.endDateMenu = false // close end date picker
      },
      modifyDates(val) {
        this.startDate = val.from
        this.endDate = val.to
      },
      retargetingRequest() {
        this.buttonClicked = true
        XandrAPI.retargetingRequest({
          placementId: this.FWPlacementID,
          requestName: this.requestName,
          startDate: this.startDate,
          endDate: this.endDate,
        })
          .then(resp => {
            Toast.success({
              title: `Retargeting Audience`,
              message: `Retargeting Audience Creation Request Succeeded`,
            })
            this.close()
          })
          .catch(err => {
            console.log('Retargeting Audience Creation Request Failed')
            Toast.error({
              title: 'Retargeting Audience',
              message: `Retargeting Audience Creation Request Failed: ${err.message}`,
            })
            this.close()
          })
      },
      open() {
        this.isOpen = true
      },
      close() {
        this.buttonClicked = false
        this.resetForm()
        this.isOpen = false
      },
      resetForm() {
        this.FWPlacementID = null
        this.requestName = null
        this.startDate = null
        this.endDate = null
        this.startDateMenu = false
        this.endDateMenu = false
        this.$refs.requestName.resetValidation()
        this.$refs.placementId.resetValidation()
      },
    },
    watch: {
      value(val) {
        this.isOpen = val
      },
      isOpen(newVal) {
        if (newVal) {
          this.open()
        }
        this.$emit('input', newVal)
      },
    },
  }
</script>
<style lang="scss">
  .custom-autocomplete .v-menu__content {
    max-width: 396px !important;
  }
  .separator {
    padding: 0 10px;
  }
</style>



// WEBPACK FOOTER //
// RetargetingRequest.vue?128f1716